import * as React from "react";

import { Resources, useResource } from "Translations/Resources";

import { CompanyFormData } from "Components/Companies/CompanyEdit/CompanyEditForm";
import { ConfluenceIcon } from "Components/Shared/Icons/ConfluenceIcon";
import { DefaultButtonWithTooltip } from "Components/Shared/Buttons/DefaultButtonWithTooltip";
import { DocumentDto } from "Api/Api";
import { EmployeeFormData } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditForm";
import { FieldError } from "react-hook-form";
import { GoogleDriveIcon } from "Components/Shared/Icons/GoogleDriveIcon";
import { PlusIcon } from "Components/Shared/Icons/PlusIcon";
import { PrimaryLink } from "Components/Shared/Routes/PrimaryLink";
import { PrimaryTooltip } from "Components/Shared/PrimaryTooltip";
import { StyledFlex } from "Components/Shared/StyledComponents";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import { TrashCanIcon } from "Components/Shared/Icons/TrashCanIcon";
import { Typography } from "@material-ui/core";
import { nameof } from "Utils/ObjectUtils";
import styled from "styled-components";
import { AutoAdderRegister } from "Hooks/useAutoAdder";

type Props = {
  title: string;
  isCompany: boolean;
  form: any;
  autoAdderRegister: AutoAdderRegister;
};

const StyledWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(auto, 450px) 1fr;
  gap: ${props => props.theme.spacing(1, 3)};
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(5)}px;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const StyledDocumentsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const StyledTrashIcon = styled.div`
  svg {
    height: 20px;
  }
`;

const StyledNewDocumentWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(auto, 195px) minmax(auto, 195px) auto 1fr;
  align-items: center;
  gap: ${props => props.theme.spacing(1, 2)};
  margin-top: ${props => props.theme.spacing(1)}px;
  grid-area: 2/1/2/4;

  & > div {
    &:nth-of-type(2) {
      grid-area: 1/1;
    }
    &:nth-of-type(2) {
      grid-area: 1/2;
    }
    &:nth-of-type(3) {
      grid-area: 1/3;
    }
    &:nth-of-type(4) {
      grid-area: 1/4;
      display: flex;
      flex-wrap: wrap;

      [class*="error-"] {
        padding-bottom: 0;
      }
    }
    &:nth-of-type(5) {
      grid-area: 2/4;
    }
  }

  @media (max-width: 850px) {
    grid-template-columns: 1fr auto;
    & > div {
      &:nth-of-type(2) {
        grid-area: 3/1;
      }
      &:nth-of-type(3) {
        grid-area: 3/2;
      }
      &:nth-of-type(4) {
        grid-area: 4/1;
      }
      &:nth-of-type(5) {
        grid-area: 4/1;
      }
    }
  }
`;

const StyledDocName = styled.div`
  flex: 1;
  padding-left: ${props => props.theme.spacing(2)}px;
  overflow: hidden;

  p {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledOverflow = styled.div`
  overflow: hidden;
`;

export const StyledError = styled.div`
  color: ${props => props.theme.colors.red};
  font-style: italic;
  margin-left: ${props => props.theme.spacing(1)}px;
  padding-bottom: ${props => props.theme.spacing(1)}px;
  align-self: flex-end;
`;

type NewDocumentErrors = {
  name?: FieldError;
  url?: FieldError;
};

const isGoogleDriveLink = (url: string) => {
  return (
    url.startsWith("https://drive.google.com/") ||
    url.startsWith("https://docs.google.com/") ||
    url.startsWith("https://blogic.atlassian.net/")
  );
};

export const getDocumentIcon = (url: string) => {
  return url.includes(".google.com") ? <GoogleDriveIcon /> : <ConfluenceIcon />;
};

export const EditDocuments: React.FunctionComponent<Props> = props => {
  const { t } = useResource();
  const { isCompany, title } = props;
  const { register, watch, setValue } = props.form;
  const { documents } = watch();

  const [newDocument, setNewDocument] = React.useState<DocumentDto>({
    documentID: 0,
    name: "",
    url: "",
  });
  const [newDocumentErrors, setNewDocumentErrors] = React.useState<
    NewDocumentErrors
  >({});

  React.useEffect(() => {
    if (isCompany) {
      register({
        name: nameof<CompanyFormData>("documents"),
      });
    } else {
      register({
        name: nameof<EmployeeFormData>("documents"),
      });
    }
    // eslint-disable-next-line
  }, [register]);

  const handleRemove = (doc: DocumentDto) => {
    if (isCompany) {
      setValue(
        nameof<CompanyFormData>("documents"),
        (documents as DocumentDto[])?.filter(x => x !== doc),
      );
    } else {
      setValue(
        nameof<EmployeeFormData>("documents"),
        (documents as DocumentDto[])?.filter(x => x !== doc),
      );
    }
  };

  props.autoAdderRegister("editDocuments", () => {
    if (newDocument && (newDocument.name || newDocument.url)) {
      return addDocument();
    } else {
      setNewDocumentErrors({});
    }

    return true;
  });

  const isValid = () => {
    const nameError = newDocument.name
      ? undefined
      : {
          type: "",
          message: t(Resources.Common.Documents.Name.Validation),
        };

    const isDriveLink = isGoogleDriveLink(newDocument.url);
    const urlError =
      newDocument.url && isDriveLink
        ? undefined
        : {
            type: "",
            message: t(
              !newDocument.url || isDriveLink
                ? Resources.Common.Documents.Url.Validation
                : Resources.Common.Documents.Url.NoDriveLink,
            ),
          };

    setNewDocumentErrors({
      name: nameError,
      url: urlError,
    });

    return !nameError && !urlError;
  };

  const addDocument = (): boolean => {
    if (isValid()) {
      documents.push(newDocument);

      if (isCompany) {
        setValue(nameof<CompanyFormData>("documents"), documents);
      } else {
        setValue(nameof<EmployeeFormData>("documents"), documents);
      }

      setNewDocument({
        ...newDocument,
        name: "",
        url: "",
      });

      return true;
    }

    return false;
  };

  return (
    <div>
      <div className={"form-title"}>{title}</div>

      <StyledWrapper>
        <StyledOverflow>
          {(documents as DocumentDto[])?.map(doc => (
            <StyledDocumentsWrapper key={doc.documentID}>
              <StyledFlex>{getDocumentIcon(doc.url)}</StyledFlex>
              <StyledDocName>
                <PrimaryTooltip title={doc.name}>
                  <Typography>{doc.name}</Typography>
                </PrimaryTooltip>
                <div>
                  <PrimaryLink
                    link={doc.url}
                    text={t(Resources.Common.Documents.Show)}
                  />
                </div>
              </StyledDocName>
              <StyledTrashIcon>
                <DefaultButtonWithTooltip
                  title={t(Resources.Common.Documents.Remove)}
                  onClick={() => handleRemove(doc)}
                >
                  <TrashCanIcon />
                </DefaultButtonWithTooltip>
              </StyledTrashIcon>
            </StyledDocumentsWrapper>
          ))}
        </StyledOverflow>
        <div></div>

        <StyledNewDocumentWrapper>
          <TextInput
            value={newDocument.url}
            placeholder={t(Resources.Common.Documents.Url.Label)}
            fieldError={newDocumentErrors.url}
            onChange={val => {
              setNewDocument({ ...newDocument, url: val.target.value });
              setNewDocumentErrors({
                ...newDocumentErrors,
                url: undefined,
              });
            }}
          />
          <TextInput
            value={newDocument.name}
            placeholder={t(Resources.Common.Documents.Name.Label)}
            fieldError={newDocumentErrors.name}
            onChange={val => {
              setNewDocument({ ...newDocument, name: val.target.value });
              setNewDocumentErrors({
                ...newDocumentErrors,
                name: undefined,
              });
            }}
          />
          <DefaultButtonWithTooltip
            title={t(Resources.Common.Documents.Add)}
            onClick={addDocument}
          >
            <PlusIcon />
          </DefaultButtonWithTooltip>

          <div>
            <StyledError className={"error-url"}>
              {newDocumentErrors.url && (
                <Typography>{newDocumentErrors.url?.message}</Typography>
              )}
            </StyledError>
            <StyledError className={"error-name"}>
              {newDocumentErrors.name && (
                <Typography>{newDocumentErrors.name?.message}</Typography>
              )}
            </StyledError>
          </div>
        </StyledNewDocumentWrapper>
      </StyledWrapper>
    </div>
  );
};
